import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, ApiError, SILENCE_SENTINEL } from '../lib/api'
import type { Approval, Attachment, Message } from '../lib/api'
import { realtime } from '../lib/realtime'
import { useMessages } from '../lib/store'
import { PartsView } from './MessageParts'
import { ApprovalCard } from './ApprovalCard'
import { BroadcastView } from './BroadcastView'
import { VoiceRecorderButton } from './VoiceRecorder'
import { Button } from './ui/button'
import { Textarea } from './ui/textarea'
import { Badge } from './ui/badge'
import { Switch } from './ui/switch'
import { Dialog, DialogContent, DialogTitle } from './ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu'
import { cn } from '../lib/utils'

/** isSilentReply reports a finished agent message whose only content is the
 *  sniff-mode silence sentinel — the client-side half of "only reply if you
 *  have something to add" (see internal/chat's silenceSentinel). It is not
 *  hidden while still streaming: a partial "[[no rep" would otherwise flash
 *  and vanish, which reads as a bug rather than as an agent staying quiet. */
export function isSilentReply(m: Message): boolean {
  if (m.role !== 'agent' || m.status === 'streaming') return false
  const text = (m.parts ?? [])
    .filter((p) => p.kind === 'text')
    .map((p) => p.text ?? '')
    .join('')
    .trim()
  return text === SILENCE_SENTINEL
}

// Must match api.maxRoomInstructions on the server, which 400s past it. The
// textarea caps typing at the same number so the limit is felt while writing
// rather than discovered on save.
const MAX_ROOM_INSTRUCTIONS = 8000

// A selector must return a stable reference for an unchanged store. `?? []`
// allocates a fresh array on every call, so useSyncExternalStore sees a new
// value each render and re-renders forever — React error #185. One shared empty
// array fixes it, and it is frozen so nothing can mutate the sentinel.
const NO_MESSAGES: readonly Message[] = Object.freeze([])
const NO_RUN_IDS: readonly string[] = Object.freeze([])

/** mergeApprovals adds approvals not already present, keeping order stable. */
function mergeApprovals(prev: Approval[], incoming: Approval[]): Approval[] {
  const seen = new Set(prev.map((a) => a.id))
  const added = incoming.filter((a) => a.id && !seen.has(a.id))
  return added.length ? [...prev, ...added] : prev
}

/** AudioAttachment plays one attachment — a recorded voice note or a
 *  synthesized reply — through the browser's native player rather than a
 *  custom one: correctness (seeking, buffering, a11y) for free, at the cost
 *  of not matching the bubble's chrome exactly. */
function AudioAttachment({ attachment }: { attachment: Attachment }) {
  return (
    <div className="mt-2">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a transcript
          renders as ordinary bubble text already; captions would duplicate it */}
      <audio controls preload="none" src={api.attachmentAudioUrl(attachment.id)} data-testid="voice-audio" />
    </div>
  )
}

/** SpeakButton requests TTS for a finished agent reply and plays it once it
 *  arrives. Hidden for a message with nothing to speak (still streaming, or
 *  the silence sentinel already suppressed by the caller) — the caller only
 *  mounts this for a message worth offering it on. */
function SpeakButton({ messageId, mine }: { messageId: string; mine: boolean }) {
  const [attachment, setAttachment] = useState<Attachment | null>(null)
  const speak = useMutation({
    mutationFn: () => api.speakMessage(messageId),
    onSuccess: setAttachment,
  })

  if (attachment) return <AudioAttachment attachment={attachment} />

  return (
    <Button
      className={cn('mt-2', mine ? 'text-white hover:bg-white/10' : 'text-muted-foreground')}
      size="xs"
      variant="ghost"
      onClick={() => speak.mutate()}
      disabled={speak.isPending}
      data-testid="speak-button"
    >
      {speak.isPending ? '🔊 …' : '🔊 Speak'}
    </Button>
  )
}

/** ForwardPicker lists this user's conversations so a message can be resent
 *  into one of them — the same forwarded-text semantics as retyping it
 *  there by hand, just without retyping it (see internal/api's
 *  handleForwardMessage). */
function ForwardPicker({ messageId, onClose }: { messageId: string; onClose: () => void }) {
  const { data: conversations = [] } = useQuery({ queryKey: ['conversations'], queryFn: api.listConversations })
  const forward = useMutation({
    mutationFn: (conversationId: string) => api.forwardMessage(messageId, conversationId),
    onSuccess: onClose,
  })

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[60vh] w-72 max-w-72 gap-0 overflow-y-auto p-0"
        data-testid="forward-overlay"
      >
        <DialogTitle className="border-b px-3 py-2 text-sm font-semibold">Forward to…</DialogTitle>
        {conversations.map((c) => (
          <button
            key={c.id}
            type="button"
            className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
            onClick={() => forward.mutate(c.id)}
            data-testid="forward-target"
          >
            {c.title || 'Untitled'}
          </button>
        ))}
      </DialogContent>
    </Dialog>
  )
}

/** MessageActions is the small row of secondary affordances every bubble
 *  gets: star (either direction), regenerate and forward (agent replies
 *  only — a user's own turn has nothing to regenerate and forwarding one's
 *  own typed message is just retyping it). */
function MessageActions({ message, mine, conversationId }: { message: Message; mine: boolean; conversationId: string }) {
  const queryClient = useQueryClient()
  const mergeMessages = useMessages((s) => s.mergeMessages)
  const [forwarding, setForwarding] = useState(false)

  const star = useMutation({
    mutationFn: (starred: boolean) => api.starMessage(message.id, starred),
    // Starring has no realtime event of its own (see handleStarMessage) — the
    // message's `starred` field lives in the Zustand transcript store, not
    // React Query, so the only way this button's own icon updates without a
    // manual reload is to fold the change back in here, the same way a
    // send response is merged in before the socket confirms it.
    onSuccess: (_data, starred) => {
      mergeMessages(conversationId, [{ ...message, starred }])
      void queryClient.invalidateQueries({ queryKey: ['starred'] })
    },
  })
  const regenerate = useMutation({ mutationFn: () => api.regenerateMessage(message.id) })

  const canRegenerate = !mine && message.status !== 'streaming'
  const dimColor = mine ? 'text-white/80 hover:bg-white/10' : 'text-muted-foreground'

  return (
    <div className="mt-1 flex items-center gap-1">
      <Button
        size="xs"
        variant="ghost"
        className={dimColor}
        aria-label={message.starred ? 'Unstar' : 'Star'}
        onClick={() => star.mutate(!message.starred)}
        data-testid="star-toggle"
      >
        {message.starred ? '⭐' : '☆'}
      </Button>
      {canRegenerate && (
        <Button
          size="xs"
          variant="ghost"
          className="text-muted-foreground"
          onClick={() => regenerate.mutate()}
          disabled={regenerate.isPending}
          data-testid="regenerate-button"
        >
          {regenerate.isPending ? '🔁 …' : '🔁 Regenerate'}
        </Button>
      )}
      <Button size="xs" variant="ghost" className={dimColor} onClick={() => setForwarding(true)} data-testid="forward-button">
        ↪ Forward
      </Button>
      {forwarding && <ForwardPicker messageId={message.id} onClose={() => setForwarding(false)} />}
    </div>
  )
}

/** Bubble is one turn.
 *
 *  senderName is resolved per message rather than passed a single fixed
 *  agent name: in a direct chat every agent bubble has the same sender, but
 *  in a group or broadcast each one can be a different agent, and the name
 *  above the bubble is the only way to tell them apart. */
function Bubble({ message, senderName, conversationId }: { message: Message; senderName: string; conversationId: string }) {
  const mine = message.role === 'user'
  const failed = message.status === 'failed'
  const hasText = (message.parts ?? []).some((p) => p.kind === 'text' && p.text)
  const canSpeak = !mine && message.status !== 'streaming' && hasText

  return (
    <div className={cn('flex px-4 py-1', mine ? 'justify-end' : 'justify-start')} data-testid={`message-${message.role}`}>
      <div
        className={cn(
          'max-w-[88%] rounded-xl px-4 py-3 text-sm md:max-w-[70%]',
          mine
            ? 'rounded-tr-sm bg-primary text-primary-foreground'
            : 'rounded-tl-sm border bg-card text-card-foreground',
        )}
      >
        {!mine && <p className="mb-1 text-xs font-semibold text-muted-foreground">{senderName}</p>}
        <PartsView parts={message.parts ?? []} />
        {/* An agent bubble with no parts yet is a run that has been accepted but
            has produced nothing: show the messenger's "typing…", not an empty box. */}
        {!mine && (message.parts ?? []).length === 0 && !failed && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="size-3 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
            <span className="text-xs">typing…</span>
          </div>
        )}
        {/* A voice note's own recording — the message text is already its
            transcript, so this is the "hear it in their voice" affordance,
            not the primary way to read it. */}
        {message.attachments?.map((a) => <AudioAttachment key={a.id} attachment={a} />)}
        {canSpeak && <SpeakButton messageId={message.id} mine={mine} />}
        {!failed && message.status !== 'streaming' && (
          <MessageActions message={message} mine={mine} conversationId={conversationId} />
        )}
      </div>
    </div>
  )
}

export function Conversation({ conversationId }: { conversationId: string }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [draft, setDraft] = useState('')
  const [approvals, setApprovals] = useState<Approval[]>([])
  const [settingsOpen, setSettingsOpen] = useState(false)
  // The room brief is edited against a local copy and saved on an explicit
  // click, unlike the two toggles next to it: it is a block of prompt every
  // member pays for on every turn, so a half-typed sentence must not become
  // what the room runs on. Re-seeded from the server's copy whenever that
  // changes, which also picks up an edit made in another tab.
  const [instructionsDraft, setInstructionsDraft] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  const messages = useMessages((s) => s.messages[conversationId] ?? NO_MESSAGES)
  const streaming = useMessages((s) => s.streaming[conversationId] ?? false)
  const activeRunIds = useMessages((s) => s.activeRunIds[conversationId] ?? NO_RUN_IDS)
  const setMessages = useMessages((s) => s.setMessages)
  const mergeMessages = useMessages((s) => s.mergeMessages)

  const { data: conversation } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => api.getConversation(conversationId),
  })

  const { data: agents = [] } = useQuery({ queryKey: ['agents'], queryFn: api.listAgents })
  const isRoom = conversation?.kind === 'group' || conversation?.kind === 'broadcast'

  const savedInstructions = conversation?.instructions ?? ''
  useEffect(() => {
    setInstructionsDraft(savedInstructions)
  }, [savedInstructions, conversationId])

  // The header/composer's single "who am I talking to" name — meaningful for
  // a direct chat, where there is exactly one agent to name.
  const primaryAgent = useMemo(
    () => agents.find((a) => a.id === conversation?.primary_agent_id),
    [agents, conversation],
  )
  const agentName = primaryAgent?.name ?? conversation?.title ?? 'Agent'

  // Every bubble's own sender, which matters once a room has more than one
  // agent: an id -> name map built from the user's whole contact list, since
  // any agent id on a message is necessarily one of theirs.
  const namesByAgentID = useMemo(() => {
    const map = new Map<string, string>()
    for (const a of agents) map.set(a.id, a.name)
    return map
  }, [agents])

  const senderName = (m: Message) =>
    (m.sender_agent_id && namesByAgentID.get(m.sender_agent_id)) || agentName

  // The room roster, for the @mention hint under the composer — the members
  // of *this* conversation, not the user's entire contact list.
  const roomMembers = useMemo(() => {
    if (!isRoom) return []
    const ids = new Set(conversation?.member_agent_ids ?? [])
    return agents.filter((a) => ids.has(a.id))
  }, [agents, conversation, isRoom])

  // Draft restore: the composer starts empty every mount, and a conversation
  // switched away from mid-sentence should come back exactly as it was left.
  // Guarded by conversationId rather than running once: navigating from one
  // conversation straight to another remounts this component with a new id
  // but React can reuse the same component instance's state across that if
  // the key/route setup ever changes, so the guard is what this depends on,
  // not an assumption about lifecycle.
  const restoredFor = useRef<string | null>(null)
  useEffect(() => {
    if (!conversation || restoredFor.current === conversationId) return
    restoredFor.current = conversationId
    if (conversation.draft) setDraft(conversation.draft)
  }, [conversation, conversationId])

  // Draft auto-save: debounced so every keystroke is not its own PATCH, and
  // skipped for the render that just restored the draft above (comparing
  // against the server's own last-known value is what tells the two apart
  // without a second ref to track "did I just set this myself").
  useEffect(() => {
    if (draft === (conversation?.draft ?? '')) return
    const id = setTimeout(() => {
      void api.updateConversation(conversationId, { draft }).catch(() => {})
    }, 800)
    return () => clearTimeout(id)
    // conversation.draft deliberately excluded: including it would re-fire
    // this effect the instant the PATCH above resolves and refetches,
    // debouncing against its own echo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, conversationId])

  // The per-contact spend meter — "what has talking to this agent cost
  // today". Direct chats only: a group or broadcast's spend is really N
  // contacts' worth, and attributing it to the room would misrepresent
  // which agent is expensive.
  const { data: usage } = useQuery({
    queryKey: ['usage', conversationId],
    queryFn: () => api.usage(conversationId),
    enabled: conversation?.kind === 'direct',
    refetchInterval: streaming ? 5_000 : false,
  })

  // The transcript is fetched once per conversation and then kept current by
  // the socket. Seeding the realtime cursor from the last seq is what makes a
  // later reconnect replay exactly the gap rather than everything.
  const { isLoading } = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: async () => {
      const fetched = await api.listMessages(conversationId)
      setMessages(conversationId, fetched)
      const last = fetched[fetched.length - 1]
      if (last) realtime.setCursor(conversationId, last.seq)
      return fetched
    },
  })

  // Opening a conversation is reading it.
  useEffect(() => {
    realtime.markRead(conversationId)
    void api.markRead(conversationId).catch(() => {})
    queryClient.invalidateQueries({ queryKey: ['conversations'] })
  }, [conversationId, queryClient])

  // Switching conversations is the only thing that clears the card list.
  //
  // It deliberately does *not* clear when a run goes inactive: a run parked on
  // an approval has no other way to be represented, and the server replays
  // pending gates on connect precisely so a card can exist without this client
  // having witnessed the run start.
  useEffect(() => {
    setApprovals([])
  }, [conversationId])

  // A gate raised while this client was between sockets is replayed on connect,
  // but a client that was merely slow to mount can still miss the window — so
  // the pending set is also fetched whenever a run becomes active. Every
  // active run is checked, not just one: a broadcast can have several agents
  // in flight at once, and any of them might be sitting on an approval.
  useEffect(() => {
    if (activeRunIds.length === 0) return
    let cancelled = false
    Promise.all(activeRunIds.map((id) => api.listApprovals(id).catch(() => [])))
      .then((lists) => {
        if (cancelled) return
        const pending = lists.flat().filter((a) => a.state === 'pending')
        setApprovals((prev) => mergeApprovals(prev, pending))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
    // Joined to a string deliberately: activeRunIds can be a fresh array
    // reference on an unrelated store update, and depending on the array
    // itself would refetch every time regardless of whether its contents
    // actually changed.
  }, [activeRunIds.join(',')])

  useEffect(() => {
    const unsubscribe = realtime.subscribe((env) => {
      if (env.conversation_id !== conversationId) return
      if (env.type === 'approval.requested') {
        // The same gate can arrive twice — once live, once from the connect
        // replay — so this merges by id rather than appending.
        setApprovals((prev) => mergeApprovals(prev, [env.data as Approval]))
      }
      if (env.type === 'approval.decided') {
        const decided = env.data as Approval
        setApprovals((prev) => prev.filter((a) => a.id !== decided.id))
      }
      if (env.type === 'resync') {
        void api.listMessages(conversationId).then((fresh) => setMessages(conversationId, fresh))
      }
    })
    return unsubscribe
  }, [conversationId, setMessages])

  // Follow the stream. `auto` rather than `smooth`: a smooth scroll restarts on
  // every token and never arrives.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'auto' })
  }, [messages, streaming])

  const send = useMutation({
    mutationFn: (text: string) => api.sendMessage(conversationId, { text }),
    onSuccess: (result) => {
      setDraft('')
      // Cleared synchronously rather than left to the debounced autosave
      // effect below: that effect's PATCH is 800ms out, and a page reload
      // shortly after send (as polish.spec.ts's usage-meter test does) kills
      // the pending timer before it fires, leaving the server's draft column
      // holding the just-sent text forever — which the next visit then
      // restores into an otherwise-empty composer.
      void api.updateConversation(conversationId, { draft: '' }).catch(() => {})

      // Every message comes straight from the response rather than being
      // waited for over the socket — the user's turn, and one placeholder per
      // agent the send addressed (zero, for a group message nobody was
      // @mentioned in). The socket only opens after Clerk mints a token, so a
      // send made moments after page load can easily precede it, and a client
      // that depended on the echo would show the user nothing until they
      // reloaded. The store de-duplicates by id, so envelopes arriving
      // afterwards are a no-op.
      const toMerge = [result.message, ...result.runs.map((r) => r.agent_message)]
      mergeMessages(conversationId, toMerge)

      // The realtime cursor is deliberately left where the initial fetch put
      // it, below these messages. That way a socket that connects a moment
      // from now replays them from the database — which is how an agent's
      // bubble gets its finished content if every delta was missed.
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })

  const stop = useMutation({
    // A broadcast or a multi-mention group send can have several runs live at
    // once; Stop ends all of them, not just one.
    mutationFn: () => Promise.all(activeRunIds.map((id) => api.stopRun(id))),
  })

  // The room settings — the two autonomy toggles from 0005_room_autonomy.sql
  // and the room brief from 0007_group_instructions.sql. All three are
  // group-only; the API 400s if any is sent for another kind, which is why the
  // panel below only renders when conversation.kind === 'group'.
  const updateRoomSettings = useMutation({
    mutationFn: (patch: {
      auto_route_mentions?: boolean
      sniff_mode?: boolean
      instructions?: string
    }) => api.updateConversation(conversationId, patch),
    onSuccess: (updated) => queryClient.setQueryData(['conversation', conversationId], updated),
  })

  // A sentinel-only reply is real in the transcript (it cost a run, it is
  // there on reload) but is not meant to be *seen* — see isSilentReply.
  const visibleMessages = useMemo(() => messages.filter((m) => !isSilentReply(m)), [messages])

  function submit() {
    const text = draft.trim()
    if (!text || send.isPending || streaming) return
    send.mutate(text)
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex items-center gap-3 border-b bg-background px-4 py-3">
        {/* On a phone the conversation replaces the contact list, so without
            this there is no way back to it — the app becomes a one-way door.
            Hidden on desktop, where both panes are visible at once. */}
        <Button
          className="inline-flex px-2 md:hidden"
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          aria-label="Back to contacts"
          data-testid="back"
        >
          ←
        </Button>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{conversation?.title ?? agentName}</p>
          <p className="text-xs text-muted-foreground">
            {streaming
              ? 'typing…'
              : isRoom
                ? `${roomMembers.length} agent${roomMembers.length === 1 ? '' : 's'}`
                : usage && usage.cost > 0
                  ? `agent · $${usage.cost.toFixed(4)} today`
                  : 'agent'}
          </p>
        </div>
        {conversation?.kind && conversation.kind !== 'direct' && (
          <Badge variant="secondary" className="hidden md:inline-flex">
            {conversation.kind}
          </Badge>
        )}
        {/* Export and room settings, inline on desktop where the header has
            room; on a phone the same five-ish controls squeezed into one row
            forced the title down to a sliver, so they fold into a dots menu
            there instead. Plain anchors, not a Button wrapping one: the
            browser's own download handling already does the right thing with
            the Content-Disposition header the server sends, and this way a
            slow export streams instead of blocking on a JS fetch-then-blob
            dance. */}
        <a
          className="hidden px-2 py-1 text-sm text-muted-foreground hover:text-foreground md:inline-flex"
          href={api.exportUrl(conversationId, 'markdown')}
          download
          aria-label="Export as Markdown"
          data-testid="export-markdown"
        >
          ⬇ MD
        </a>
        <a
          className="hidden px-2 py-1 text-sm text-muted-foreground hover:text-foreground md:inline-flex"
          href={api.exportUrl(conversationId, 'json')}
          download
          aria-label="Export as JSON"
          data-testid="export-json"
        >
          ⬇ JSON
        </a>
        {conversation?.kind === 'group' && (
          <Button
            className="hidden md:inline-flex"
            variant="ghost"
            size="sm"
            onClick={() => setSettingsOpen((v) => !v)}
            aria-label="Room settings"
            data-testid="room-settings-toggle"
          >
            ⚙
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="inline-flex md:hidden"
              variant="ghost"
              size="icon-sm"
              aria-label="Conversation options"
              data-testid="conversation-header-menu-trigger"
            >
              ⋮
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem asChild>
              <a href={api.exportUrl(conversationId, 'markdown')} download data-testid="export-markdown-mobile">
                ⬇ Export as Markdown
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={api.exportUrl(conversationId, 'json')} download data-testid="export-json-mobile">
                ⬇ Export as JSON
              </a>
            </DropdownMenuItem>
            {conversation?.kind === 'group' && (
              <DropdownMenuItem onClick={() => setSettingsOpen((v) => !v)} data-testid="room-settings-toggle-mobile">
                ⚙ Room settings
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* All three settings only mean anything for a group, so this panel does
          not exist for a direct or broadcast conversation — see the mutationFn
          above, which the server would 400 on for either. */}
      {conversation?.kind === 'group' && settingsOpen && (
        <div className="flex flex-col gap-2 border-b bg-muted/40 px-4 py-3" data-testid="room-settings-panel">
          <label className="flex items-start gap-3">
            <Switch
              checked={conversation.auto_route_mentions}
              onCheckedChange={(checked) => updateRoomSettings.mutate({ auto_route_mentions: checked })}
              className="mt-0.5"
              data-testid="auto-route-toggle"
            />
            <span>
              <span className="block text-sm">Let agents @mention each other</span>
              <span className="block text-xs text-muted-foreground">
                An agent's own reply can wake another member it mentions, up to a few hops — off by
                default to avoid runaway token spend.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3">
            <Switch
              checked={conversation.sniff_mode}
              onCheckedChange={(checked) => updateRoomSettings.mutate({ sniff_mode: checked })}
              className="mt-0.5"
              data-testid="sniff-mode-toggle"
            />
            <span>
              <span className="block text-sm">Agents listen in without being mentioned</span>
              <span className="block text-xs text-muted-foreground">
                A plain message reaches every member; each one only replies if it has something to
                add. Costs one run per member per message even when everyone stays quiet.
              </span>
            </span>
          </label>

          <div className="border-t pt-2">
            <p className="text-sm">Room instructions</p>
            <p className="mb-2 text-xs text-muted-foreground">
              What this room is about, its rules, its topic. Every member is given this on every
              turn, on top of whatever you set on the contact itself.
            </p>
            <Textarea
              value={instructionsDraft}
              onChange={(e) => setInstructionsDraft(e.target.value)}
              placeholder="e.g. We are triaging production incidents. Be terse, cite the log line you are reading from, and say so when you are guessing."
              rows={4}
              maxLength={MAX_ROOM_INSTRUCTIONS}
              className="bg-background"
              data-testid="room-instructions-input"
            />
            <div className="mt-2 flex items-center gap-3">
              <Button
                size="xs"
                disabled={instructionsDraft === savedInstructions || updateRoomSettings.isPending}
                onClick={() => updateRoomSettings.mutate({ instructions: instructionsDraft })}
                data-testid="room-instructions-save"
              >
                Save
              </Button>
              <span className="text-xs text-muted-foreground">
                {instructionsDraft === savedInstructions
                  ? 'Saved'
                  : `Unsaved — ${instructionsDraft.length}/${MAX_ROOM_INSTRUCTIONS}`}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto py-3" data-testid="transcript">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="size-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 px-8">
            <p className="font-medium">{isRoom ? `Say something to the room` : `Say something to ${agentName}`}</p>
            <p className="text-center text-sm text-muted-foreground">
              {isRoom && conversation?.kind === 'group'
                ? 'Address an agent with @Name — nobody replies until someone is mentioned.'
                : 'The run happens on the server, so you can close this tab and the reply still finishes.'}
            </p>
          </div>
        ) : conversation?.kind === 'broadcast' ? (
          <BroadcastView messages={visibleMessages} senderName={senderName} />
        ) : (
          visibleMessages.map((m) => (
            <Bubble key={m.id} message={m} senderName={senderName(m)} conversationId={conversationId} />
          ))
        )}

        {approvals.map((approval) => (
          <ApprovalCard
            key={approval.id}
            approval={approval}
            onDecided={(id) => setApprovals((prev) => prev.filter((a) => a.id !== id))}
          />
        ))}

        <div ref={bottomRef} />
      </div>

      {send.isError && (
        <div className="bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {send.error instanceof ApiError ? send.error.message : 'Could not send that message.'}
        </div>
      )}

      <div className="border-t bg-background">
        {/* The room roster, so a user knows who they can @mention without
            having to remember. Group only — a broadcast has no mentions to
            type, every send already reaches everyone. */}
        {conversation?.kind === 'group' && roomMembers.length > 0 && (
          <div className="flex flex-wrap gap-2 px-3 pt-2 text-xs text-muted-foreground">
            <span>Mention:</span>
            {roomMembers.map((a) => (
              <button
                key={a.id}
                type="button"
                className="text-primary"
                onClick={() => setDraft((d) => (d ? `${d.trimEnd()} @${a.name.replace(/\s+/g, '')} ` : `@${a.name.replace(/\s+/g, '')} `))}
                data-testid="mention-suggestion"
              >
                @{a.name.replace(/\s+/g, '')}
              </button>
            ))}
          </div>
        )}

        {/* Slash commands: typing "/" offers this contact's own tools by
            name, purely as a composer convenience — there is no special
            wire format for it, the inserted text is plain prose the agent
            reads like anything else it might parse a "/tool" mention out of. */}
        {conversation?.kind === 'direct' &&
          (primaryAgent?.tools?.length ?? 0) > 0 &&
          /^\/\S*$/.test(draft) &&
          (() => {
            const fragment = draft.slice(1).toLowerCase()
            const matches = (primaryAgent!.tools ?? []).filter((t) => t.name.toLowerCase().startsWith(fragment))
            if (matches.length === 0) return null
            return (
              <div className="flex flex-col gap-1 px-3 pt-2 text-sm">
                {matches.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="rounded-md px-2 py-1 text-left hover:bg-muted"
                    onClick={() => setDraft(`/${t.name} `)}
                    data-testid="slash-command-suggestion"
                  >
                    <span className="font-medium">/{t.name}</span>
                    {t.description && <span className="text-muted-foreground"> — {t.description}</span>}
                  </button>
                ))}
              </div>
            )
          })()}

        <div className="flex items-end gap-2 p-3">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, shift+enter is a newline — the messenger convention.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                submit()
              }
            }}
            placeholder={
              conversation?.kind === 'broadcast'
                ? `Broadcast to ${roomMembers.length || 'every'} agent${roomMembers.length === 1 ? '' : 's'}`
                : conversation?.kind === 'group'
                  ? 'Message the room — @Name to address an agent'
                  : `Message ${agentName}`
            }
            rows={1}
            className="max-h-40 resize-none"
            data-testid="composer"
            disabled={send.isPending}
          />
          {!streaming && !draft.trim() && (
            <VoiceRecorderButton
              conversationId={conversationId}
              disabled={send.isPending}
              onSent={(result) => {
                mergeMessages(conversationId, [result.message, ...result.runs.map((r) => r.agent_message)])
                queryClient.invalidateQueries({ queryKey: ['conversations'] })
              }}
            />
          )}
          {streaming ? (
            <Button variant="outline" onClick={() => stop.mutate()} data-testid="stop">
              Stop
            </Button>
          ) : (
            <Button onClick={submit} disabled={!draft.trim() || send.isPending} data-testid="send">
              {send.isPending ? 'Sending…' : 'Send'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
